import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FaRegStopCircle, FaMicrophone } from "react-icons/fa";
import { Plus, X, FileText, File, Check, RotateCcw } from "lucide-react";

import { ChatInputProps, CachedFileUpload } from '../../types/types';
import { lightenColor } from "../../constants/lightenColor";
import { isLightColor } from "../../utils/LuminosityChecker";
import { darkenColor } from "../../constants/darkenColor";
import { speechToText, uploadAndExtractFile, deleteUploadedFile, UploadAndExtractResponse } from '../../services/api/KaiConfigData';

// Stable counter for generating unique entry IDs (avoids index-based tracking bugs)
let _entryIdCounter = 0;

// Per-file upload tracking
type FileUploadEntry = {
  id: number;
  file: File;
  status: 'uploading' | 'success' | 'error';
  result?: UploadAndExtractResponse;
  error?: string;
};

// Helper function to get file icon based on file type
const getFileIcon = (fileName: string, iconColor: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  const iconSize = 16;

  if (['pdf'].includes(ext)) {
    return <FileText size={iconSize} style={{ color: '#ef4444' }} />;
  } else if (['doc', 'docx'].includes(ext)) {
    return <FileText size={iconSize} style={{ color: '#2563eb' }} />;
  } else if (['ppt', 'pptx'].includes(ext)) {
    return <FileText size={iconSize} style={{ color: '#ea580c' }} />;
  } else if (['xls', 'xlsx'].includes(ext)) {
    return <FileText size={iconSize} style={{ color: '#16a34a' }} />;
  } else if (['csv'].includes(ext)) {
    return <FileText size={iconSize} style={{ color: '#16a34a' }} />;
  } else if (['txt'].includes(ext)) {
    return <FileText size={iconSize} style={{ color: iconColor }} />;
  } else if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(ext)) {
    return <File size={iconSize} style={{ color: '#8b5cf6' }} />;
  }
  return <File size={iconSize} style={{ color: iconColor }} />;
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const ChatInput: React.FC<ChatInputProps & {themeOverride?: 'light' | 'dark' | 'auto'}> = ({
  input,
  setInput,
  onSendMessage,
  configData,
  loading,
  pendingMsgCount,
  allowSendMsg,
  messages,
  activeChatId,
  isSidebarOpen,
  themeOverride = 'auto',
  sysConfig,
  activeFiles,
  onRemoveActiveFile
}) => {
  // Derive configurable limits from sysConfig with defaults
  const maxFiles = Number(sysConfig?.file_upload_max_files) || 5;
  const maxSizeMB = Number(sysConfig?.file_upload_max_size_mb) || 10;
  const allowedExtensions = (sysConfig?.file_upload_allowed_types || '.pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx,.csv,.png,.jpg,.jpeg').split(',').map(s => s.trim());
  const allowedFormatLabels = Array.from(new Set(allowedExtensions.map(ext => {
    const e = ext.toLowerCase().replace('.', '');
    if (e === 'pdf') return 'PDF';
    if (['doc', 'docx'].includes(e)) return 'Word';
    if (['ppt', 'pptx'].includes(e)) return 'PPT';
    if (['xls', 'xlsx'].includes(e)) return 'Excel';
    if (e === 'csv') return 'CSV';
    if (e === 'txt') return 'TXT';
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(e)) return 'Images';
    return e.toUpperCase();
  }))).join(', ');
  // App config takes priority, then sys_config, default to enabled
  const fileUploadEnabled = String(configData?.user_file_upload_enabled ?? sysConfig?.file_upload_enabled ?? 'true') === 'true';
  const speechToTextEnabled = String(configData?.speech_to_text_enabled ?? sysConfig?.speech_to_text_enabled ?? 'true') === 'true';

  // File upload state - per-file status tracking
  const [fileEntries, setFileEntries] = useState<FileUploadEntry[]>([]);
  const [uploadError, setUploadError] = useState<string>("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const attachMenuRef = useRef<HTMLDivElement | null>(null);

  // Speech-to-text state (Azure)
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const { app_id } = useParams();
  const netId = localStorage.getItem('net_id') || '';

  // Derived state
  const hasFiles = fileEntries.length > 0;
  const hasUploadingFiles = fileEntries.some(e => e.status === 'uploading');
  const successfulFiles = fileEntries.filter(e => e.status === 'success');

  // Clear all files after sending
  const clearAllFiles = () => {
    setFileEntries([]);
    setUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Upload a single file immediately (identified by stable entry ID)
  const uploadFile = async (file: File, entryId: number) => {
    try {
      const result = await uploadAndExtractFile(file, netId, app_id || '', activeChatId);
      setFileEntries(prev => prev.map(entry =>
        entry.id === entryId ? { ...entry, status: 'success' as const, result } : entry
      ));
    } catch (err: any) {
      setFileEntries(prev => prev.map(entry =>
        entry.id === entryId ? { ...entry, status: 'error' as const, error: err.message || 'Upload failed' } : entry
      ));
    }
  };

  // Retry a failed upload (capture entry before state update to avoid stale reads)
  const retryUpload = (entryId: number) => {
    const entry = fileEntries.find(e => e.id === entryId);
    if (!entry) return;
    setFileEntries(prev => prev.map(e =>
      e.id === entryId ? { ...e, status: 'uploading' as const, error: undefined } : e
    ));
    uploadFile(entry.file, entryId);
  };

  // Remove a file — if already uploaded, delete from backend too
  const removeFile = async (entryId: number) => {
    const entry = fileEntries.find(e => e.id === entryId);

    // Remove from local state immediately
    setFileEntries(prev => prev.filter(e => e.id !== entryId));
    setUploadError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // If it was successfully uploaded, delete from vector store + blob
    if (entry?.status === 'success' && entry.result?.file_id && entry.result?.chunk_count) {
      try {
        await deleteUploadedFile(
          entry.result.file_id,
          entry.result.chunk_count,
          netId,
          app_id || '',
          activeChatId
        );
      } catch (err) {
        console.error('Failed to delete uploaded file from backend:', err);
      }
    }
  };

  // File upload handler — validate then upload immediately
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError("");
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newFiles = Array.from(selectedFiles);
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    // Check total count
    if (fileEntries.length + newFiles.length > maxFiles) {
      setUploadError(`Maximum ${maxFiles} files allowed. You already have ${fileEntries.length} file(s).`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Validate each file
    for (const file of newFiles) {
      const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
      if (!allowedExtensions.includes(ext)) {
        setUploadError(`Unsupported file type: ${file.name}. Allowed: ${allowedExtensions.join(', ')}`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (file.size > maxSizeBytes) {
        setUploadError(`File too large: ${file.name}. Maximum size is ${maxSizeMB}MB.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    // Add files with 'uploading' status and stable IDs, then trigger immediate upload
    const newEntries: FileUploadEntry[] = newFiles.map(file => ({
      id: ++_entryIdCounter,
      file,
      status: 'uploading' as const
    }));

    setFileEntries(prev => [...prev, ...newEntries]);

    // Trigger uploads for each new file using stable entry IDs
    newEntries.forEach(entry => {
      uploadFile(entry.file, entry.id);
    });

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Build cached results for send
  const buildCachedResults = (): CachedFileUpload[] => {
    return successfulFiles
      .filter(e => e.result)
      .map(e => ({
        fileName: e.result!.file_name,
        fileId: e.result!.file_id,
        chunkCount: e.result!.chunk_count,
        isIndexed: e.result!.is_indexed,
        summary: e.result!.summary,
        blobUrl: e.result!.blob_url,
        fileType: e.result!.file_type,
      }));
  };

  // Handle send
  const handleSend = () => {
    if (loading) return;
    if (hasUploadingFiles) return;
    if (pendingMsgCount <= 0) return;
    if (input.trim() === "" && successfulFiles.length === 0) return;

    const cachedResults = successfulFiles.length > 0 ? buildCachedResults() : undefined;
    onSendMessage(input, cachedResults);
    clearAllFiles();
    if (inputRef.current) {
      inputRef.current.style.height = '54px';
    }
  };

  // Speech-to-text handler using Azure Speech SDK
  const toggleSpeechToText = async () => {
    if (isListening) {
      // Stop recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      return;
    }

    // Check for microphone support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert('Microphone access is not supported in this browser.');
      return;
    }

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsListening(false);
        setIsTranscribing(true);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        try {
          // Send to Azure Speech-to-Text API
          const result = await speechToText(audioBlob, netId, app_id || '');

          if (result.success && result.text) {
            const currentInput = inputRef.current?.value || '';
            setInput(currentInput + result.text + ' ');
          }
        } catch (error) {
          console.error('Error transcribing audio:', error);
          alert('Failed to transcribe audio. Please try again.');
        } finally {
          setIsTranscribing(false);
          audioChunksRef.current = [];
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setIsListening(false);
        stream.getTracks().forEach(track => track.stop());
      };

      // Start recording
      mediaRecorder.start();
      setIsListening(true);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Unable to access microphone. Please ensure microphone permissions are granted.');
    }
  };

  // Inject subtle mic pulse animation
  useEffect(() => {
    const styleId = 'mic-pulse-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes mic-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.85; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Close attach menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) {
        setShowAttachMenu(false);
      }
    };
    if (showAttachMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttachMenu]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [linkRequestModal, setIsLinkRequestModalOpen] = useState(false);
  const [isMobileState, setIsMobileState] = useState(false);

  // Use configData colors as default with theme override
  const backgroundColor = configData.app_ui_settings.ui_backgroundcolor;
  const configIsLight = isLightColor(backgroundColor);

  // Determine effective theme based on themeOverride
  const effectiveIsLight = themeOverride === 'auto' ? configIsLight : themeOverride === 'light';

  // Use theme-aware colors
  const chatBackground = themeOverride === 'auto'
    ? backgroundColor || (configIsLight ? '#ffffff' : '#212121')
    : (effectiveIsLight ? '#ffffff' : '#212121');

  // ChatGPT-style input background - subtle variation of config color
  const createInputVariation = (baseColor: string, isLight: boolean) => {
    if (!baseColor || !baseColor.startsWith("#")) return baseColor;

    const hex = baseColor.slice(1);
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;

    // More noticeable but still subtle adjustment for input background
    const adjustment = isLight ? -8 : 8; // Darker for light themes, lighter for dark themes
    const newR = Math.max(0, Math.min(255, r + adjustment));
    const newG = Math.max(0, Math.min(255, g + adjustment));
    const newB = Math.max(0, Math.min(255, b + adjustment));

    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  const inputBackground = themeOverride === 'auto'
    ? backgroundColor // Same as background for auto theme
    : themeOverride === 'light'
    ? backgroundColor // Same as background for light theme
    : '#2d2d30'; // Proper dark theme input color (like ChatGPT)

  const inputBorderColor = themeOverride === 'auto'
    ? (configIsLight ? darkenColor(backgroundColor, 8) : lightenColor(backgroundColor, 8))
    : (effectiveIsLight ? '#e5e5e5' : '#404040');

  // ChatGPT-style button colors
  const buttonEnabledBg = effectiveIsLight ? '#000000' : '#ffffff';
  const buttonEnabledColor = effectiveIsLight ? '#ffffff' : '#000000';
  const buttonDisabledBg = effectiveIsLight ? '#e5e7eb' : '#374151';
  const buttonDisabledColor = effectiveIsLight ? '#9ca3af' : '#6b7280';

  const lighterInputBoxBackground = inputBackground;

  const themeTextColor = themeOverride === 'auto'
    ? configData.app_ui_settings.ui_fontcolor
    : (effectiveIsLight ? '#000000' : '#ffffff');

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobileState(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (messages.length === 0) {
      // Focus the textarea for new chat
      inputRef.current?.focus();
    }
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      if (pendingMsgCount <= 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        const textArea = e.currentTarget;
        const { selectionStart, selectionEnd } = textArea;

        const newText =
          input.substring(0, selectionStart) +
          "\n" +
          input.substring(selectionEnd);

        setInput(newText);

        setTimeout(() => {
          textArea.selectionStart = textArea.selectionEnd = selectionStart + 1;
        }, 0);
      } else {
        e.preventDefault();
        handleSend();
      }
    }
  };

  // Can send if there's text or at least one successful file, and nothing is still uploading
  const canSend = (input.trim() !== "" || successfulFiles.length > 0) && !hasUploadingFiles && pendingMsgCount > 0;

  return (
    <div
      className={`flex flex-col w-full transition-all duration-300  ${
        messages.length === 0
          ? "mx-auto px-4 max-w-7xl"
          : "fixed bottom-0 z-10 px-4 pb-6"
      }`}
      style={{
        backgroundColor: messages.length > 0 ? chatBackground : 'transparent',
        left: messages.length > 0 ? (isSidebarOpen && !isMobileState ? '256px' : (!isMobileState ? '52px' : '0')) : 'auto',
        right: messages.length > 0 ? '0' : 'auto',
        width: messages.length > 0 ? (isSidebarOpen && !isMobileState ? 'calc(100vw - 256px)' : '100vw') : '100%'
      }}
    >
      {/* Input Section */}
      <div className={messages.length > 0 ? "w-full max-w-4xl mx-auto" : "w-full max-w-2xl mx-auto"}>
        {String(allowSendMsg) === "false" && (
          <div
            className="w-full text-center py-3 px-4 rounded-lg flex items-center justify-center"
            style={{
              background: lighterInputBoxBackground,
              fontWeight: "500",
              fontSize: "14px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
            }}
          >
            <span className="text-red-500">Maximum permitted message quota exceeded for this month please contact course owner to increase the quota</span>
            {linkRequestModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full">
                  <h2 className="text-lg font-semibold mb-4">Coming Soon</h2>
                  <p>This feature will be available shortly. Stay tuned!</p>
                  <button
                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    onClick={() => setIsLinkRequestModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Input Container */}
        <div
          className="relative"
          style={{
            background: inputBackground,
            border: `1px solid ${inputBorderColor}`,
            borderRadius: hasFiles ? '20px' : '9999px',
            boxShadow:
              themeOverride === 'auto'
                ? configIsLight
                  ? '0 2px 6px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)'
                  : '0 2px 6px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.15)'
                : effectiveIsLight
                ? '0 2px 6px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)'
                : '0 2px 6px rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.15)',
          }}
        >
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept={allowedExtensions.join(',')}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            multiple
          />

          {/* File Attachment Display - inside input, above textarea */}
          {hasFiles && (
            <div className="px-4 pt-3 pb-1 flex flex-wrap gap-2">
              {fileEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg max-w-xs"
                  style={{
                    backgroundColor: effectiveIsLight ? '#f3f4f6' : '#374151',
                    border: `1px solid ${
                      entry.status === 'error'
                        ? '#ef4444'
                        : entry.status === 'success'
                        ? (effectiveIsLight ? '#d1fae5' : '#065f46')
                        : (effectiveIsLight ? '#e5e7eb' : '#4b5563')
                    }`
                  }}
                >
                  {/* File icon or status icon */}
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded flex items-center justify-center"
                    style={{ backgroundColor: effectiveIsLight ? '#e5e7eb' : '#4b5563' }}
                  >
                    {entry.status === 'uploading' ? (
                      <svg className="animate-spin h-4 w-4" style={{ color: themeTextColor }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : entry.status === 'success' ? (
                      <Check size={16} style={{ color: '#16a34a' }} />
                    ) : (
                      getFileIcon(entry.file.name, themeTextColor)
                    )}
                  </div>
                  <div className="flex flex-col min-w-0 flex-1">
                    <span
                      className="text-sm font-medium truncate"
                      style={{ color: themeTextColor, maxWidth: '150px' }}
                      title={entry.file.name}
                    >
                      {entry.file.name}
                    </span>
                    <span
                      className="text-xs"
                      style={{
                        color: entry.status === 'error'
                          ? '#ef4444'
                          : entry.status === 'uploading'
                          ? (effectiveIsLight ? '#6b7280' : '#9ca3af')
                          : '#16a34a'
                      }}
                    >
                      {entry.status === 'uploading'
                        ? 'Uploading...'
                        : entry.status === 'error'
                        ? (entry.error || 'Failed')
                        : formatFileSize(entry.file.size)}
                    </span>
                  </div>

                  {/* Action button: retry for error, remove for others */}
                  {entry.status === 'error' ? (
                    <button
                      onClick={() => retryUpload(entry.id)}
                      className="flex-shrink-0 p-1 rounded-full hover:bg-opacity-80 transition-colors"
                      style={{
                        backgroundColor: effectiveIsLight ? '#fecaca' : '#7f1d1d',
                        color: '#ef4444'
                      }}
                      title="Retry upload"
                    >
                      <RotateCcw size={14} />
                    </button>
                  ) : null}
                  <button
                    onClick={() => removeFile(entry.id)}
                    className="flex-shrink-0 p-1 rounded-full hover:bg-opacity-80 transition-colors"
                    style={{
                      backgroundColor: effectiveIsLight ? '#d1d5db' : '#4b5563',
                      color: themeTextColor
                    }}
                    title="Remove file"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Error Message */}
          {uploadError && (
            <div className="px-4 pt-2">
              <span className="text-xs text-red-500">{uploadError}</span>
            </div>
          )}

          {/* Input row */}
          <div className="relative flex items-center">
            {/* Attachment Button - left (controlled by user_file_upload_enabled) */}
            {fileUploadEnabled && <div ref={attachMenuRef} className="absolute left-2 z-20">
              <button
                onClick={() => setShowAttachMenu(!showAttachMenu)}
                className="p-2 rounded-full transition-all duration-200 cursor-pointer"
                style={{
                  color: effectiveIsLight ? '#6b7280' : '#9ca3af',
                  backgroundColor: showAttachMenu
                    ? (effectiveIsLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)')
                    : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!showAttachMenu) {
                    e.currentTarget.style.backgroundColor = effectiveIsLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showAttachMenu) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                title={`Attach file (${allowedFormatLabels} — max ${maxSizeMB}MB)`}
              >
                <Plus size={20} />
              </button>

              {/* Attach Menu Popover */}
              {showAttachMenu && (
                <div
                  className="absolute bottom-full left-0 mb-2 rounded-xl shadow-lg border py-1"
                  style={{
                    backgroundColor: effectiveIsLight ? '#ffffff' : '#2d2d30',
                    borderColor: effectiveIsLight ? '#e5e7eb' : '#404040',
                    minWidth: '200px',
                  }}
                >
                  <button
                    onClick={() => {
                      fileInputRef.current?.click();
                      setShowAttachMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors"
                    style={{ color: themeTextColor }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = effectiveIsLight ? '#f3f4f6' : '#3a3a3d';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <FileText size={16} />
                    <div className="flex flex-col items-start">
                      <span>Attach file</span>
                      <span
                        className="text-xs"
                        style={{ color: effectiveIsLight ? '#9ca3af' : '#6b7280' }}
                      >
                        {`${allowedFormatLabels} (max ${maxSizeMB}MB, up to ${maxFiles} files)`}
                      </span>
                    </div>
                  </button>
                </div>
              )}
            </div>}

            {/* Textarea */}
            <textarea
              ref={inputRef}
              className="w-full bg-transparent focus:outline-none resize-none overflow-hidden transition-all duration-200 placeholder:text-gray-500 placeholder:font-normal placeholder:text-base placeholder:font-inter"
              value={input}
              style={{
                color: themeTextColor,
                lineHeight: '20px',
                height: '54px',
                minHeight: '54px',
                maxHeight: '200px',
                fontSize: '16px',
                padding: fileUploadEnabled ? '17px 110px 17px 56px' : '17px 110px 17px 20px',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                overflow: 'hidden',
              }}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything"
              onKeyDown={handleKeyDown}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                if (target.value === '' && !hasFiles) {
                  target.style.height = "54px";
                } else {
                  target.style.height = "54px";
                  if (target.scrollHeight > 54) {
                    target.style.height = `${Math.min(target.scrollHeight, 200)}px`;
                  }
                }
              }}
            />

            {/* Speech-to-text Button (controlled by speech_to_text_enabled) */}
            {speechToTextEnabled && (
            <button
              onClick={toggleSpeechToText}
              disabled={isTranscribing}
              className={`absolute right-14 p-2 rounded-full transition-all duration-200 ${
                isTranscribing ? 'cursor-wait opacity-70' : ''
              }`}
              style={{
                backgroundColor: isListening || isTranscribing
                  ? (effectiveIsLight ? '#000000' : '#ffffff')
                  : 'transparent',
                color: isListening || isTranscribing
                  ? (effectiveIsLight ? '#ffffff' : '#000000')
                  : (effectiveIsLight ? '#6b7280' : '#9ca3af'),
                ...(isListening ? { animation: 'mic-pulse 1.5s ease-in-out infinite' } : {})
              }}
              title={isTranscribing ? 'Transcribing...' : isListening ? 'Stop recording' : 'Voice input'}
            >
              {isTranscribing ? (
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <FaMicrophone className="h-5 w-5" />
              )}
            </button>
            )}

            {/* Send Button */}
            <button
              onClick={() => {
                if (loading) {
                  // Handle stop functionality
                } else if (canSend) {
                  handleSend();
                }
              }}
              className={`absolute right-2 cursor-pointer p-2 ${
                !canSend && !loading
                  ? "cursor-not-allowed opacity-50"
                  : ""
              }`}
              disabled={!canSend && !loading}
              style={{ display: 'inline-block', paddingTop: '3px', borderRadius: '50%' }}
            >
              {loading ? (
                <FaRegStopCircle
                  className="h-8 w-8"
                  style={{ color: effectiveIsLight ? '#000000' : '#ffffff' }}
                />
              ) : (
                <svg
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  className="h-8 w-8"
                  height="1em"
                  width="1em"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ color: effectiveIsLight ? '#000000' : '#ffffff' }}
                >
                  <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM385 231c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0l-71-71V376c0 13.3-10.7 24-24 24s-24-10.7-24-24V193.9l-71 71c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9L239 119c9.4-9.4 24.6-9.4 33.9 0L385 231z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Active files from previous messages */}
        {activeFiles && activeFiles.length > 0 && fileEntries.length === 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 px-1">
            {(activeFiles.length <= 5 ? activeFiles : activeFiles.slice(0, 4)).map((file) => (
              <div
                key={file.fileId || file.name}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs"
                style={{
                  backgroundColor: effectiveIsLight ? '#f3f4f6' : '#374151',
                  border: `1px solid ${effectiveIsLight ? '#e5e7eb' : '#4b5563'}`,
                  color: themeTextColor,
                }}
              >
                {getFileIcon(file.name, themeTextColor)}
                <span className="truncate max-w-[120px]" title={file.name}>
                  {file.name}
                </span>
                {onRemoveActiveFile && file.fileId && (
                  <button
                    onClick={() => onRemoveActiveFile(file.fileId!)}
                    className="flex-shrink-0 p-0.5 rounded-full hover:bg-opacity-80 transition-colors"
                    style={{
                      color: effectiveIsLight ? '#9ca3af' : '#6b7280',
                    }}
                    title="Remove file from chat"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
            {activeFiles.length > 5 && (
              <div
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs"
                style={{
                  backgroundColor: effectiveIsLight ? '#f3f4f6' : '#374151',
                  border: `1px solid ${effectiveIsLight ? '#e5e7eb' : '#4b5563'}`,
                  color: effectiveIsLight ? '#6b7280' : '#9ca3af',
                }}
              >
                +{activeFiles.length - 4} more
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInput;
